/**
 * Quick Switcher (Cmd/Ctrl-K command palette)
 * Fuzzy/substring search across channels, existing DM threads and people.
 * Full keyboard navigation: ↑/↓ to move, Enter to select, Esc to close.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Hash, X } from 'lucide-react';
import { useChannels } from '@/hooks/useChannels';
import { useDMThreads, useUsers, useCreateDMThread } from '@/hooks/useDMs';
import { useAppStore } from '@/store/app.store';
import { useAuthStore } from '@/store/auth.store';
import Avatar from '@/components/common/Avatar';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import type { Channel, DMThread, User } from '@/types/api.types';

interface QuickSwitcherProps {
  onClose: () => void;
}

type ResultItem =
  | { type: 'channel'; id: string; label: string; data: Channel }
  | { type: 'dm'; id: string; label: string; data: DMThread }
  | { type: 'user'; id: string; label: string; sublabel: string; data: User };

const GROUP_LABELS: Record<ResultItem['type'], string> = {
  channel: 'Канали',
  dm: 'Лични съобщения',
  user: 'Хора',
};

const dmDisplayName = (thread: DMThread, currentUserId?: string): string => {
  const others = thread.members?.filter((m) => m.id !== currentUserId) || [];
  if (others.length === 0) return 'Разговор';
  if (thread.is_group) {
    return others.map((m) => m.display_name?.split(' ')[0] || m.email).join(', ');
  }
  return others[0]?.display_name || others[0]?.email || 'Разговор';
};

const QuickSwitcher = ({ onClose }: QuickSwitcherProps) => {
  const { user: currentUser } = useAuthStore();
  const { setActiveChannel, setActiveDMThread } = useAppStore();
  const { data: channels } = useChannels();
  const { data: dmThreads } = useDMThreads();
  const { data: users } = useUsers();
  const createDMThread = useCreateDMThread();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpening, setIsOpening] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { dialogRef } = useDialogA11y(onClose, { autoFocus: false });

  // Build the flat, ordered result list (channels → DMs → people).
  const items = useMemo<ResultItem[]>(() => {
    const q = query.trim().toLowerCase();

    const channelItems: ResultItem[] = (channels || [])
      .filter((c) => !c.is_archived)
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .map((c) => ({ type: 'channel' as const, id: c.id, label: c.name, data: c }));

    const dmItems: ResultItem[] = (dmThreads || [])
      .map((t) => ({ thread: t, name: dmDisplayName(t, currentUser?.id) }))
      .filter(({ name }) => !q || name.toLowerCase().includes(q))
      .map(({ thread, name }) => ({ type: 'dm' as const, id: thread.id, label: name, data: thread }));

    // People: only surface when actively searching to keep the default list tight.
    // Exclude self.
    const userItems: ResultItem[] = q
      ? (users || [])
          .filter((u) => u.id !== currentUser?.id)
          .filter(
            (u) =>
              (u.display_name || '').toLowerCase().includes(q) ||
              (u.email || '').toLowerCase().includes(q)
          )
          .map((u) => ({
            type: 'user' as const,
            id: u.id,
            label: u.display_name || u.email,
            sublabel: (u as User & { is_bot?: boolean }).is_bot ? 'AI Асистент' : u.email,
            data: u as User,
          }))
      : [];

    return [...channelItems, ...dmItems, ...userItems];
  }, [channels, dmThreads, users, query, currentUser?.id]);

  // Reset highlight to the top whenever the query changes (adjust state during render).
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    setActiveIndex(0);
  }

  // Focus the search field on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = useCallback(
    async (item: ResultItem) => {
      if (item.type === 'channel') {
        setActiveChannel(item.id);
        onClose();
      } else if (item.type === 'dm') {
        setActiveDMThread(item.id);
        onClose();
      } else {
        // Open or create a 1:1 DM with the selected person.
        try {
          setIsOpening(true);
          const thread = await createDMThread.mutateAsync(item.id);
          setActiveDMThread(thread.id);
          onClose();
        } catch {
          toast.error('Грешка при създаване на разговора');
          setIsOpening(false);
        }
      }
    },
    [setActiveChannel, setActiveDMThread, createDMThread, onClose]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[activeIndex];
      if (item) handleSelect(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Track running index across groups so keyboard nav maps to a single flat list.
  let runningIndex = -1;
  const groupsOrder: ResultItem['type'][] = ['channel', 'dm', 'user'];

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-start justify-center z-50 pt-[12vh]"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Бързо превключване"
        className="bg-white dark:bg-dark-bg-elevated rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-dark-border-primary">
          <svg
            className="w-5 h-5 text-gray-400 dark:text-dark-text-muted flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls="quickswitcher-listbox"
            aria-activedescendant={items.length > 0 ? `qs-option-${activeIndex}` : undefined}
            aria-autocomplete="list"
            aria-label="Търси канали, съобщения и хора"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Търси канали, съобщения и хора..."
            className="flex-1 bg-transparent text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-muted border-none focus:outline-none text-sm"
          />
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary"
            aria-label="Затвори"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2" role="listbox" id="quickswitcher-listbox" aria-label="Резултати">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-dark-text-muted">
              Няма резултати
            </div>
          ) : (
            groupsOrder.map((groupType) => {
              const groupItems = items.filter((it) => it.type === groupType);
              if (groupItems.length === 0) return null;
              return (
                <div key={groupType} role="group" aria-label={GROUP_LABELS[groupType]}>
                  <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted" aria-hidden="true">
                    {GROUP_LABELS[groupType]}
                  </div>
                  {groupItems.map((item) => {
                    runningIndex += 1;
                    const index = runningIndex;
                    const isActive = index === activeIndex;
                    return (
                      <button
                        key={`${item.type}-${item.id}`}
                        data-index={index}
                        role="option"
                        id={`qs-option-${index}`}
                        aria-selected={isActive}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIndex(index)}
                        disabled={isOpening}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors disabled:opacity-50 ${
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary'
                        }`}
                      >
                        {item.type === 'channel' ? (
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary flex items-center justify-center">
                            <Hash className="w-4 h-4 text-gray-500 dark:text-dark-text-muted" />
                          </div>
                        ) : item.type === 'dm' ? (
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary flex items-center justify-center text-gray-500 dark:text-dark-text-muted text-xs font-semibold">
                            {item.label.charAt(0).toUpperCase()}
                          </div>
                        ) : (
                          <div className="flex-shrink-0">
                            <Avatar user={item.data} size="sm" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
                            {item.type === 'channel' ? `# ${item.label}` : item.label}
                          </div>
                          {item.type === 'user' && (
                            <div className="text-xs text-gray-500 dark:text-dark-text-muted truncate">
                              {item.sublabel}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-dark-border-primary flex items-center gap-3 text-[11px] text-gray-400 dark:text-dark-text-muted">
          <span>↑↓ навигация</span>
          <span>↵ избор</span>
          <span>Esc затвори</span>
        </div>
      </div>
    </div>
  );
};

export default QuickSwitcher;
